import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import Confetti from "react-confetti";
import { ROUTES } from "../../../shared/utils";

export const Completion: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const connectionState =
    (location.state as {
      deviceConnected?: boolean;
      provider?: string;
    } | null) || null;
  const deviceConnected = Boolean(connectionState?.deviceConnected);
  const provider = connectionState?.provider;

  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));
  const [recycleConfetti, setRecycleConfetti] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const confettiTimer = window.setTimeout(() => {
      setRecycleConfetti(false);
    }, 6000);

    const navigationTimer = window.setTimeout(() => {
      navigate(ROUTES.DASHBOARD);
    }, 8000);

    return () => {
      window.clearTimeout(confettiTimer);
      window.clearTimeout(navigationTimer);
    };
  }, [navigate]);

  const handleGoToDashboard = () => {
    navigate(ROUTES.DASHBOARD);
  };

  const showConfetti = useMemo(
    () => windowSize.width > 0 && windowSize.height > 0,
    [windowSize.height, windowSize.width]
  );

  const title = deviceConnected
    ? "Wearable connected!"
    : "You're officially ready!";
  const subtitle = deviceConnected
    ? `Your ${
        provider
          ? provider.charAt(0).toUpperCase() + provider.slice(1)
          : "wearable"
      } is all set. Head over to your dashboard to see what\'s next.`
    : "Thanks for sharing your details. We are excited to have you join the PEAK4 programme!";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 3, md: 6 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={recycleConfetti}
          numberOfPieces={320}
          gravity={0.28}
          tweenDuration={4800}
        />
      )}

      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 4,
          p: { xs: 4, md: 5 },
          textAlign: "center",
          borderColor: "transparent",
        }}
      >
        <Stack spacing={3} alignItems="center">
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                bgcolor: "success.light",
                color: "success.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TaskAltRoundedIcon sx={{ fontSize: 48 }} />
            </Box>
          </Stack>

          <Stack spacing={2} alignItems="center">
            <Typography variant="h4" component="h6">
              {title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          </Stack>

          <Button variant="outlined" size="large" onClick={handleGoToDashboard}>
            Go to Dashboard
          </Button>
        </Stack>
      </Card>
    </Box>
  );
};
